import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminMainText,
  AdminPanelTitle,
  AdminRowAction,
  AdminSeverityText,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminMetadataText,
  adminFormTextareaClasses,
  withoutEscalatedAuditLogEntries,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
const formatNoteDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

const ManualReviewCaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemFromState = location.state?.item;

  const [activeTab, setActiveTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewMedia, setPreviewMedia] = useState(null);
  
  // Audit log filters state
  const [auditFilters, setAuditFilters] = useState({
    dateRange: 'All',
    actor: 'All',
    module: 'All',
    action: 'All',
    outcome: 'All'
  });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [actorOpen, setActorOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  const dateRangeRef = useRef(null);
  const actorRef = useRef(null);
  const moduleRef = useRef(null);
  const actionRef = useRef(null);
  const outcomeRef = useRef(null);
  
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(actorRef, () => setActorOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(actionRef, () => setActionOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));

  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
      
      const caseData = itemFromState || {
        id: `MC#${caseId}`,
        caseId: `MC#${caseId}`,
        reportType: 'Customer Report',
        entityType: 'Post',
        entityId: 'POST#0001',
        reporter: 'John D.',
        reporterType: 'Customer',
        severity: 'Level 2 — Medium',
        status: 'New',
        assignedTo: null,
        dateSubmitted: new Date().toISOString(),
      };

      const now = new Date();
      const receivedAt = new Date(caseData.dateSubmitted);
      const firstViewedAt = new Date(receivedAt.getTime() + 2 * 60 * 60 * 1000);
      const lastUpdatedAt = new Date(receivedAt.getTime() + 6 * 60 * 60 * 1000);

      // Generate hash for consistent images
      const hashId = caseId ? String(caseId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 12345;
      const baseSeed = hashId % 10000;
      
      setDetail({
        ...caseData,
        receivedAt: receivedAt.toISOString(),
        firstViewedAt: firstViewedAt.toISOString(),
        lastUpdatedAt: lastUpdatedAt.toISOString(),
        timeInQueue: Math.floor((now.getTime() - receivedAt.getTime()) / (1000 * 60 * 60)),
        reporterId: 'CUS-12345',
        reporterNotes: 'This post contains inappropriate content.',
        category: 'Harassment',
        additionalContext: '',
        reporterContact: 'customer@example.com',
        // Content & Evidence
        content: {
          gallery: [
            { url: `https://picsum.photos/seed/${baseSeed}1/640/400`, flagged: false },
            { url: `https://picsum.photos/seed/${baseSeed}2/640/400`, flagged: true },
            { url: `https://picsum.photos/seed/${baseSeed}3/640/400`, flagged: false },
          ],
          attachments: [
            { name: 'screenshot-1.png', url: `https://picsum.photos/seed/${baseSeed}4/800/600`, type: 'screenshot', size: '1.2 MB' },
            { name: 'screenshot-2.png', url: `https://picsum.photos/seed/${baseSeed}5/800/600`, type: 'screenshot', size: '0.9 MB' },
          ],
          messageThread: caseData.entityType === 'Message' ? 'Full message thread content...' : null,
          reviewText: caseData.entityType === 'Review' ? 'Full review text content...' : null,
          postContent: caseData.entityType === 'Post' ? 'Full post content...' : null,
          metadata: { timestamp: formatDateTime(receivedAt.toISOString()), ipAddress: '192.168.1.100' },
        },
        // Report Details
        reportDetails: {
          reporterType: caseData.reporterType,
          reporterName: caseData.reporter,
          reporterId: 'CUS-12345',
          reporterContact: 'customer@example.com',
          reportCategory: 'Harassment',
          reportSubcategory: 'Abusive Language',
          severitySuggested: 'Level 2 — Medium',
          evidenceProvided: true,
          reporterNotes: caseData.reporterNotes,
        },
        // Flag / Admin Input
        adminFlags: [
          {
            flaggedBy: 'Admin: R.B.',
            flagCategory: 'Profile Mismatch',
            internalReason: 'Identity inconsistency detected',
            notes: 'Profile details do not match submitted documents',
            timestamp: formatDateTime(firstViewedAt.toISOString()),
          },
        ],
        // Audit Logs
        auditLogs: [
          {
            timestamp: formatDateTime(receivedAt.toISOString()),
            performedBy: 'System',
            module: 'Reports',
            event: 'Case created',
            details: `Manual review case ${caseData.caseId} created`,
            outcome: 'Success',
            ref: { id: caseData.caseId, type: 'case' }
          },
          {
            timestamp: formatDateTime(firstViewedAt.toISOString()),
            performedBy: 'Admin: R.B.',
            module: 'Moderation',
            event: 'Case viewed',
            details: `Case ${caseData.caseId} viewed for first time`,
            outcome: 'Success',
            ref: { id: caseData.caseId, type: 'case' }
          },
          {
            timestamp: formatDateTime(lastUpdatedAt.toISOString()),
            performedBy: 'Admin: R.B.',
            module: 'Moderation',
            event: 'Status changed',
            details: `Case status changed from New to Active`,
            outcome: 'Success',
            ref: { id: caseData.caseId, type: 'case' }
          },
        ],
        // Admin Notes
        notes: [
          {
            id: 'note-1',
            timestamp: formatDateTime(lastUpdatedAt.toISOString()),
            author: 'Rachel Brown',
            adminName: 'Moderator — Rachel Brown',
            email: 'admin@tradesposter.com',
            category: 'Support',
            content: 'Reviewing case details. Evidence appears sufficient for action.',
            outcome: 'Pending',
            linkedRef: { id: caseData.caseId, type: 'case' }
          },
        ],
        // Enforcement Actions
        availableActions: ['Hide', 'Remove', 'Restrict', 'Lock Profile', 'Suspend Account', 'Block Messaging', 'Close Case'],
        // Escalation
        escalation: {
          triggers: [
            { condition: 'L4 severity', action: 'Immediate escalation', status: 'Not Triggered' },
            { condition: 'Violent threats', action: 'Escalate to Risk & Safety', status: 'Not Triggered' },
            { condition: 'Repeated harassment', action: 'Escalate to Compliance', status: 'Not Triggered' },
          ],
          escalateTo: ['Compliance', 'Risk & Safety', 'Senior Moderators'],
        },
        // Violation Creation
        violationCreation: {
          canCreate: true,
          violationType: 'Harassment',
          severity: caseData.severity,
          entityType: caseData.entityType,
          entityId: caseData.entityId,
          linkedCaseId: caseData.caseId,
        },
      });
      
      setLoading(false);
    };

    loadDetail();
  }, [caseId, itemFromState]);

  const renderOverview = () => {
    if (!detail) return null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel>Case ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.caseId}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Report Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.reportType}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Entity Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.entityType}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Entity ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.entityId}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Severity Level</AdminDetailFieldLabel>
            <AdminSeverityText severity={detail.severity}>{detail.severity}</AdminSeverityText>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
            <AdminStatusBadge status={detail.status} label={detail.status} />
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Assigned Admin</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.assignedTo || 'Unassigned'}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Received Date</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{formatDateTime(detail.receivedAt)}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>First Viewed</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{formatDateTime(detail.firstViewedAt)}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{formatDateTime(detail.lastUpdatedAt)}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Time in Queue</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.timeInQueue || 0} hours</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Reporter Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.reporterType}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Reporter ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.reporterId || '—'}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Reporter Notes</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.reporterNotes || '—'}</AdminDetailFieldValue>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel>Category</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{detail.category || '—'}</AdminDetailFieldValue>
          </div>
        </div>
    );
  };

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'content', label: 'Content & Evidence' },
    { id: 'report', label: 'Report Details' },
    { id: 'flag', label: 'Flag / Admin Input' },
    { id: 'enforcement', label: 'Enforcement Actions' },
    { id: 'escalation', label: 'Escalation' },
    { id: 'violation', label: 'Violation Creation' },
    { id: 'audit', label: 'Audit Log' },
    { id: 'notes', label: 'Admin Notes' },
  ], []);

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    setAuditFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Generate CSV content
  const generateCSV = (logs, filters) => {
    const headers = ['Timestamp (AEST)', 'Performed By', 'Module', 'Event', 'Details', 'Outcome', 'Ref'];
    const rows = logs.map(log => [
      log.timestamp,
      log.performedBy || '—',
      log.module || '—',
      log.event || '—',
      log.details || '—',
      log.outcome || '—',
      log.ref?.id || '—'
    ]);
    
    const csvRows = [headers, ...rows];
    return csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  };

  // Admin Notes handlers
  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = formatDateTime(now.toISOString());
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      timestamp: timestamp,
      createdAt: now.toISOString(),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      content: noteForm.noteText.trim(),
      noteContent: noteForm.noteText.trim()
    };
    
    setDetail(prev => ({
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    }));
    
    closeNotesModal();
  };

  const getRefLink = (ref) => {
    if (!ref || !ref.id) return null;
    switch (ref.type) {
      case 'case':
        return `/admin/moderation/manual/${ref.id.replace('MC#', '')}`;
      case 'post':
        return `/admin/posts/${ref.id.replace('POST#', '')}`;
      default:
        return null;
    }
  };

  const getRefDisplay = (ref) => {
    if (!ref || !ref.id) return '—';
    return ref.id;
  };

  // Filter logs based on current filters
  const filteredAuditLogs = useMemo(() => {
    if (!detail || !detail.auditLogs) return [];
    
    let logs = [...detail.auditLogs];
    
    if (auditFilters.actor !== 'All') {
      logs = logs.filter(log => log.performedBy === auditFilters.actor);
    }
    
    if (auditFilters.module !== 'All') {
      logs = logs.filter(log => log.module === auditFilters.module);
    }
    
    if (auditFilters.action !== 'All') {
      logs = logs.filter(log => log.event === auditFilters.action);
    }
    
    if (auditFilters.outcome !== 'All') {
      logs = logs.filter(log => log.outcome === auditFilters.outcome);
    }
    
    return withoutEscalatedAuditLogEntries(logs);
  }, [detail?.auditLogs, auditFilters]);

  const renderContentAndEvidence = () => {
    if (!detail || !detail.content) return null;
    
    const { content } = detail;
    
    return (
      <>
        <div className="space-y-6">
            {/* Media Gallery */}
            {content.gallery && content.gallery.length > 0 && (
              <div className="space-y-4">
                <AdminPanelTitle className="text-offblack">Media</AdminPanelTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {content.gallery.map((item, index) => (
                    <button
                      key={item.url || index}
                      type="button"
                      onClick={() => setPreviewMedia(item)}
                      className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={item.url}
                        alt={`Evidence image ${index + 1}`}
                        className="h-32 w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300?text=Image';
                        }}
                      />
                      {item.flagged && (
                        <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                          Flagged
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Screenshots */}
            {content.attachments && content.attachments.filter(att => att.type === 'screenshot').length > 0 && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Screenshots</AdminPanelTitle>
                <div className="space-y-2">
                  {content.attachments
                    .filter(att => att.type === 'screenshot')
                    .map((file, idx) => (
                      <div key={idx} className="border border-lightgray rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-lightgray rounded flex items-center justify-center">
                            <span className="text-base" aria-hidden>📷</span>
                          </div>
                          <div>
                            <div className="text-sm text-offblack font-medium">{file.name}</div>
                            <AdminMetadataText>{file.size || 'Unknown size'}</AdminMetadataText>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-primaryblue hover:text-offblack text-sm font-medium"
                          onClick={() => window.open(file.url, '_blank')}
                        >
                          View
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Content based on entity type */}
            {(content.messageThread || content.reviewText || content.postContent) && (
              <div className="space-y-4">
                <AdminPanelTitle className="text-offblack">Content</AdminPanelTitle>
                <div className="border border-lightgray rounded-lg p-4">
                  <AdminMainText className="text-offblack whitespace-pre-line">
                    {content.messageThread || content.reviewText || content.postContent}
                  </AdminMainText>
                </div>
              </div>
            )}

            {/* Metadata */}
            {content.metadata && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Metadata</AdminPanelTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(content.metadata).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <AdminDetailFieldLabel>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </AdminDetailFieldLabel>
            <AdminDetailFieldValue>{value || '—'}</AdminDetailFieldValue>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Full Image Viewer Modal */}
        {previewMedia && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="relative max-w-6xl w-full max-h-[90vh]"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium transition-colors"
                onClick={() => setPreviewMedia(null)}
              >
                Close
              </button>
              <div className="relative rounded-lg overflow-hidden bg-white">
                <img 
                  src={previewMedia.url} 
                  alt="Full image view" 
                  className="w-full h-full object-contain max-h-[90vh]" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800?text=Image+Not+Available';
                  }}
                />
                {previewMedia.flagged && (
                  <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-alertred text-white text-xs uppercase tracking-wide">
                    Flagged
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderReportDetails = () => {
    if (!detail || !detail.reportDetails) return null;
    
    const { reportDetails } = detail;
    
    return (
        <div className="space-y-6">
          {/* Reporter Information */}
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack">Reporter Information</AdminPanelTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Reporter Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reporterType}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Reporter Name</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reporterName}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Reporter ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reporterId}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Reporter Contact</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reporterContact}</AdminDetailFieldValue>
              </div>
            </div>
          </div>

          {/* Report Classification */}
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack">Report Classification</AdminPanelTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Report Category</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reportCategory}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Report Subcategory</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.reportSubcategory || '—'}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Severity Suggested by Reporter</AdminDetailFieldLabel>
                <AdminSeverityText severity={reportDetails.severitySuggested}>{reportDetails.severitySuggested || '—'}</AdminSeverityText>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Evidence Provided</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{reportDetails.evidenceProvided ? 'Yes' : 'No'}</AdminDetailFieldValue>
              </div>
            </div>
          </div>

          {/* Reporter Notes */}
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack">Reporter Notes</AdminPanelTitle>
            <div className="border border-lightgray rounded-lg p-4">
              <AdminMainText className="text-offblack whitespace-pre-line">
                {reportDetails.reporterNotes || '—'}
              </AdminMainText>
            </div>
          </div>
        </div>
    );
  };

  const renderFlagAdminInput = () => {
    if (!detail || !detail.adminFlags) return null;
    
    return (
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="overflow-hidden">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1fr) minmax(150px, 1.5fr) minmax(200px, 2fr) minmax(250px, 2.5fr) minmax(140px, 1.4fr)' }}>
            <div className="flex items-center">
              <AdminTableHeader>Flagged By</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Flag Category</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Internal Reason</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Notes</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Timestamp</AdminTableHeader>
            </div>
          </div>
          
          {detail.adminFlags.length === 0 ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                No admin flags recorded for this case.
              </AdminSupportingText>
            </div>
          ) : (
            detail.adminFlags.map((flag, idx) => (
              <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1fr) minmax(150px, 1.5fr) minmax(200px, 2fr) minmax(250px, 2.5fr) minmax(140px, 1.4fr)' }}>
                <div className="flex items-center">
                  <AdminTableBody>{flag.flaggedBy}</AdminTableBody>
                </div>
                <div className="flex items-center">
                  <AdminTableBody>{flag.flagCategory}</AdminTableBody>
                </div>
                <div className="flex items-center">
                  <AdminTableBody>{flag.internalReason}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="text-offblack truncate" title={flag.notes}>{flag.notes || '—'}</AdminTableBody>
                </div>
                <div className="flex items-center">
                  <AdminTableBody>{flag.timestamp}</AdminTableBody>
                </div>
              </div>
            ))
          )}
        </div>
      </DashboardMainContainer>
    );
  };

  const renderAuditLog = () => {
    if (!detail || !detail.auditLogs) return null;
    
    return (
      <div className="space-y-4">
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="flex gap-3 flex-wrap">
              {/* Date Range Filter */}
              <div className="relative" ref={dateRangeRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setDateRangeOpen(!dateRangeOpen);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Date range: {auditFilters.dateRange}</span>
                  <i className={`fi ${dateRangeOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {dateRangeOpen && (
                  <div className="flex flex-col rounded w-36 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Last 7 days', 'Last 30 days', 'Custom'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          handleFilterChange('dateRange', option);
                          setDateRangeOpen(false);
                        }}
                        className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Performed By Filter */}
              <div className="relative" ref={actorRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setActorOpen(!actorOpen);
                    setDateRangeOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Performed By: {auditFilters.actor}</span>
                  <i className={`fi ${actorOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {actorOpen && (
                  <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'System', 'Admin: R.B.', 'Admin: S.J.', 'Moderator: J.S.'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          handleFilterChange('actor', option);
                          setActorOpen(false);
                        }}
                        className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Module Filter */}
              <div className="relative" ref={moduleRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setModuleOpen(!moduleOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setActionOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Module: {auditFilters.module}</span>
                  <i className={`fi ${moduleOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {moduleOpen && (
                  <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Reports', 'Moderation', 'Compliance', 'Support'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          handleFilterChange('module', option);
                          setModuleOpen(false);
                        }}
                        className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Filter */}
              <div className="relative" ref={actionRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setActionOpen(!actionOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setOutcomeOpen(false);
                  }}
                >
                  <span>Event: {auditFilters.action}</span>
                  <i className={`fi ${actionOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {actionOpen && (
                  <div className="flex flex-col rounded w-48 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Case created', 'Case viewed', 'Status changed', 'Assignment change', 'Flag added'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          handleFilterChange('action', option);
                          setActionOpen(false);
                        }}
                        className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Outcome Filter */}
              <div className="relative" ref={outcomeRef}>
                <AdminFilterDropdownButton
                  onClick={() => {
                    setOutcomeOpen(!outcomeOpen);
                    setDateRangeOpen(false);
                    setActorOpen(false);
                    setModuleOpen(false);
                    setActionOpen(false);
                  }}
                >
                  <span>Outcome: {auditFilters.outcome}</span>
                  <i className={`fi ${outcomeOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'} lg:text-[14px] cursor-pointer flex items-center ml-3`}></i>
                </AdminFilterDropdownButton>
                {outcomeOpen && (
                  <div className="flex flex-col rounded w-36 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                    {['All', 'Success', 'Failed', 'Blocked', 'Reverted'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          handleFilterChange('outcome', option);
                          setOutcomeOpen(false);
                        }}
                        className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Export CSV Button */}
              <AdminHeaderAction
              onClick={() =>{
                const csvContent = generateCSV(filteredAuditLogs, auditFilters);
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `case-audit-logs-${detail.caseId}-${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Export CSV</AdminHeaderAction>
            </div>
          </div>
        
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="overflow-hidden">
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(180px, 1.8fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
              <div className="flex items-center">
                <AdminTableHeader>Timestamp (AEST)</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Performed By</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Module</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Event</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Details</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Outcome</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Ref</AdminTableHeader>
              </div>
            </div>
            
            {/* Rows */}
            {filteredAuditLogs.length === 0 ? (
              <div className="py-10 text-center">
              <AdminSupportingText>
                No audit logs found matching the filters.
              </AdminSupportingText>
              </div>
            ) : (
              filteredAuditLogs.map((log, idx) => {
                const refLink = getRefLink(log.ref);
                const outcomeColor = log.outcome === 'Success' 
                  ? 'bg-freshgreen' 
                  : log.outcome === 'Failed'
                  ? 'bg-alertred'
                  : log.outcome === 'Blocked'
                  ? 'bg-orange-500'
                  : log.outcome === 'Reverted'
                  ? 'bg-warmyellow'
                  : 'bg-mediumgray';

                return (
                  <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(180px, 1.8fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr)' }}>
                    <div className="flex items-center">
                      <AdminTableBody>{log.timestamp}</AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody>{log.performedBy || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody>{log.module || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody>{log.event || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="text-offblack truncate" title={log.details}>{log.details || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs text-white ${outcomeColor}`}>
                        {log.outcome || '—'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {refLink && log.ref?.id ? (
                        <Link
                          to={refLink}
                          className="inline-flex px-2 py-1 rounded bg-lightgray text-offblack text-xs font-medium hover:bg-primaryblue hover:text-white transition-colors"
                        >
                          {log.ref.id}
                        </Link>
                      ) : (
                        <AdminTableBody className="text-mediumgray">—</AdminTableBody>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderAdminNotes = () => {
    if (!detail) return null;
    
    return (
      <div className="space-y-4">
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end items-center w-full">
              <AdminHeaderAction
              onClick={() =>openNotesModal()}
              >
                Add Note</AdminHeaderAction>
            </div>
          </div>
          
          {/* Notes Table */}
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
              <div className="flex items-center">
                <AdminTableHeader>Created At</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Added By</AdminTableHeader>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Note</AdminTableHeader>
              </div>
            </div>
            
            {/* Rows - Sort by date (newest first) */}
            {(() => {
              const sortedNotes = [...(detail.notes || [])].sort((a, b) => {
                const aDate = new Date(a.createdAt || a.timestamp || 0);
                const bDate = new Date(b.createdAt || b.timestamp || 0);
                return bDate - aDate;
              });

              return sortedNotes.length > 0 ? sortedNotes.map((note) => {
                const noteContent = note.noteContent || note.content || '';
                const createdAt = note.createdAt ? formatNoteDateTime(note.createdAt) : 
                                 (note.timestamp ? formatNoteDateTime(new Date(note.timestamp)) : '—');
                
                const addedByDisplay = getAdminNoteAddedByDisplay(note);

                return (
                  <div key={note.id} className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                    <div className="flex items-start">
                      <span className="">{createdAt}</span>
                    </div>
                    <div className="flex items-start">
                      <AdminTableBody emphasis>{addedByDisplay}</AdminTableBody>
                    </div>
                    <div className="flex items-start">
                      <span className="whitespace-pre-wrap">{noteContent}</span>
                    </div>
                  </div>
                );
              }) : (
                <div className="py-8 text-center">
                  <span className="text-mediumgray">No notes yet</span>
                </div>
              );
            })()}
          </DashboardMainContainer>

          {/* Add Note Modal */}
          {notesModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <AdminPanelTitle className="text-offblack">
                    Add Note
                  </AdminPanelTitle>
                  <button
                    onClick={closeNotesModal}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rr-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Note - Required */}
                  <div>
                    <AdminDetailFieldLabel>
                      Note <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <textarea
                      value={noteForm.noteText}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                      className={adminFormTextareaClasses()}
                      placeholder="Enter note..."
                      required
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4">
                    <AdminFormSecondaryAction type="button" onClick={closeNotesModal}
                      >Cancel</AdminFormSecondaryAction>
              <AdminHeaderAction
              onClick={handleNoteSave}
                    >Save Note</AdminHeaderAction>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderEnforcementActions = () => {
    if (!detail || !detail.availableActions) return null;
    
    return (
        <div className="space-y-6">
          {detail.availableActions && detail.availableActions.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {detail.availableActions.map((action) => (
                <AdminHeaderAction
                  key={action}
                  onClick={() => {
                    if (window.confirm(`Apply action: ${action}?`)) {
                      console.log(`Action applied: ${action}`);
                    }
                  }}
                >
                  {action}
                </AdminHeaderAction>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText>
                No enforcement actions available for this case.
              </AdminSupportingText>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-lightgray">
            <AdminPanelTitle className="text-offblack mb-3">
              Action Information
            </AdminPanelTitle>
            <div className="space-y-2">
              <AdminSupportingText>• All enforcement actions are permanent and cannot be undone without admin approval</AdminSupportingText>
              <AdminSupportingText>• Each action automatically creates an audit log entry with timestamp and admin details</AdminSupportingText>
              <AdminSupportingText>• Actions may affect the case status, severity level, or queue status</AdminSupportingText>
              <AdminSupportingText>• Review the Audit Logs tab to see all action history</AdminSupportingText>
            </div>
          </div>
        </div>
    );
  };

  const renderEscalation = () => {
    if (!detail || !detail.escalation) return null;
    
    const { escalation } = detail;
    
    return (
        <div className="space-y-6">
          {/* Escalation Triggers */}
          {escalation.triggers && escalation.triggers.length > 0 && (
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack">Escalation Triggers</AdminPanelTitle>
              <div className="space-y-2">
                {escalation.triggers.map((trigger, idx) => (
                  <div key={idx} className="border border-lightgray rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="text-sm text-offblack font-medium">{trigger.condition}</div>
                      <AdminSupportingText>{trigger.action}</AdminSupportingText>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs text-white ${
                        trigger.status === 'Triggered'
                          ? 'bg-freshgreen'
                          : 'bg-mediumgray'
                      }`}>
                        {trigger.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Escalate To */}
          {escalation.escalateTo && escalation.escalateTo.length > 0 && (
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack">Escalate To</AdminPanelTitle>
              <div className="flex flex-wrap gap-2">
                {escalation.escalateTo.map((target, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-lightgray text-offblack text-sm font-medium">
                    {target}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
    );
  };

  const renderViolationCreation = () => {
    if (!detail || !detail.violationCreation) return null;
    
    const { violationCreation } = detail;
    
    return (
        <div className="space-y-4">
            <AdminPanelTitle className="text-offblack">Violation Creation</AdminPanelTitle>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Can Create Violation</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{violationCreation.canCreate ? 'Yes' : 'No'}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Violation Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{violationCreation.violationType || '—'}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Severity</AdminDetailFieldLabel>
                <AdminSeverityText severity={violationCreation.severity}>{violationCreation.severity || '—'}</AdminSeverityText>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Entity Type</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{violationCreation.entityType || '—'}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Entity ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{violationCreation.entityId || '—'}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Linked Case ID</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{violationCreation.linkedCaseId || '—'}</AdminDetailFieldValue>
              </div>
            </div>

            {violationCreation.canCreate && (
              <div className="pt-4 border-t border-lightgray">
              <AdminHeaderAction
              onClick={() =>{
                    if (window.confirm('Create violation from this case?')) {
                      console.log('Violation creation initiated');
                    }
                  }}
                >
                  Create Violation</AdminHeaderAction>
              </div>
            )}
        </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'content':
        return renderContentAndEvidence();
      case 'report':
        return renderReportDetails();
      case 'flag':
        return renderFlagAdminInput();
      case 'enforcement':
        return renderEnforcementActions();
      case 'escalation':
        return renderEscalation();
      case 'violation':
        return renderViolationCreation();
      case 'audit':
        return renderAuditLog();
      case 'notes':
        return renderAdminNotes();
      default:
        return renderOverview();
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-mediumgray">Loading case details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="space-y-2">
                <AdminPanelTitle className="text-offblack">
                  {detail.caseId} — {detail.reportType}
                </AdminPanelTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <AdminSeverityText severity={detail.severity}>{detail.severity}</AdminSeverityText>
                  <AdminStatusBadge status={detail.status} label={detail.status} />
                </div>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-3 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div className={adminDetailTabContentClassName(activeTab)}>
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ManualReviewCaseDetail;

